import { useEffect, useState } from 'react'
import { db, type ReminderRule } from '../lib/db'
import { calcAllReminders, type ReminderState } from '../lib/reminders'
import { getCurrentOdometer } from '../lib/sync'
import { saveReminderRule, deleteReminderRule } from '../lib/sync'
import {
  isPushSupported, isStandalone,
  getCurrentSubscription, subscribePush, unsubscribePush,
} from '../lib/push'
import ReminderCard from './reminder/ReminderCard'
import ReminderRuleForm from './reminder/ReminderRuleForm'

type View = 'list' | 'add' | 'edit'
type PushState = 'loading' | 'unsupported' | 'need-standalone' | 'off' | 'on' | 'working'

export default function ReminderPage() {
  const [states, setStates]   = useState<ReminderState[]>([])
  const [rules, setRules]     = useState<ReminderRule[]>([])
  const [currentKm, setCurrentKm] = useState(0)
  const [view, setView]       = useState<View>('list')
  const [editing, setEditing] = useState<ReminderRule | null>(null)
  const [pushState, setPushState] = useState<PushState>('loading')

  async function load() {
    const [rawRules, expenses, km] = await Promise.all([
      db.reminder_rules.toArray(),
      db.expenses.filter(e => e.deleted_at === null).toArray(),
      getCurrentOdometer(),
    ])
    setRules(rawRules as ReminderRule[])
    setCurrentKm(km)
    const today = new Date().toISOString().slice(0, 10)
    setStates(calcAllReminders(rawRules as ReminderRule[], expenses, km, today))
  }

  async function loadPushState() {
    if (!isPushSupported()) { setPushState('unsupported'); return }
    if (!isStandalone())    { setPushState('need-standalone'); return }
    const sub = await getCurrentSubscription()
    setPushState(sub ? 'on' : 'off')
  }

  useEffect(() => { load(); loadPushState() }, [])

  async function handleSave(rule: ReminderRule) {
    await saveReminderRule(rule)
    await load()
    setView('list')
    setEditing(null)
  }

  async function handleDelete(id: string) {
    if (!confirm('確定刪除此提醒規則？')) return
    await deleteReminderRule(id)
    await load()
  }

  function handleEdit(rule: ReminderRule) {
    setEditing(rule)
    setView('edit')
  }

  async function handleTogglePush() {
    setPushState('working')
    if (pushState === 'on') {
      await unsubscribePush()
      setPushState('off')
    } else {
      const result = await subscribePush()
      setPushState(result === 'subscribed' ? 'on' : result === 'denied' ? 'off' : 'off')
      if (result === 'denied') alert('通知權限被拒絕，請至 iPhone 設定 → Safari → 通知 中開啟。')
      if (result === 'error') alert('訂閱失敗，請重試。')
    }
  }

  if (view === 'add' || view === 'edit') {
    return (
      <ReminderRuleForm
        initial={editing ?? undefined}
        onSave={handleSave}
        onCancel={() => { setView('list'); setEditing(null) }}
      />
    )
  }

  return (
    <div className="reminder-page">
      <header className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">提醒</h1>
          {currentKm > 0 && (
            <p className="page-header-sub">目前里程 {currentKm.toLocaleString()} km</p>
          )}
        </div>
        <button className="add-rule-btn" onClick={() => setView('add')}>＋ 新增</button>
      </header>

      {/* 推播通知區塊 */}
      <div className="push-section">
        {pushState === 'loading' && null}
        {pushState === 'unsupported' && (
          <p className="push-hint">此裝置不支援推播通知。</p>
        )}
        {pushState === 'need-standalone' && (
          <div className="push-banner">
            <p>想在手機收到提醒通知？</p>
            <p className="push-hint">請用 Safari 開啟本網站，點「分享」→「加入主畫面」，再從主畫面開啟 App。</p>
          </div>
        )}
        {(pushState === 'off' || pushState === 'on' || pushState === 'working') && (
          <div className="push-banner">
            <div className="push-row">
              <span>{pushState === 'on' ? '推播通知已開啟' : '推播通知未開啟'}</span>
              <button
                className={`push-toggle${pushState === 'on' ? ' on' : ''}`}
                onClick={handleTogglePush}
                disabled={pushState === 'working'}
              >
                {pushState === 'working' ? '…' : pushState === 'on' ? '關閉' : '開啟通知'}
              </button>
            </div>
            {pushState === 'off' && (
              <p className="push-hint">開啟後，每天早上 8 點會推送即將到期的提醒。</p>
            )}
          </div>
        )}
      </div>

      {states.length === 0 && rules.length === 0 && (
        <p className="placeholder">點「新增規則」加入機油、輪胎、驗車等週期提醒。</p>
      )}

      <div className="reminder-list">
        {states.map(s => (
          <div key={s.rule.id}>
            <ReminderCard state={s} />
            <div className="card-actions" style={{ marginTop: -4, marginBottom: 6 }}>
              <button className="card-btn" onClick={() => handleEdit(s.rule)}>編輯</button>
              <button className="card-btn danger" onClick={() => handleDelete(s.rule.id)}>刪除</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
